import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Zap, X, Search, ArrowLeftRight } from "lucide-react";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { fetchTeams } from "../lib/api";
import { Flag } from "../components/Flag";

const AXES = [
  { key: "squad_value", label: "SQUAD VALUE" },
  { key: "top5_presence", label: "TOP-5 LEAGUES" },
  { key: "powerhouse", label: "PEDIGREE" },
  { key: "age_curve", label: "AGE CURVE" },
  { key: "form", label: "FORM" },
];

const COLOR_A = "#00B8FF"; // bluez
const COLOR_B = "#FF3D3D"; // redz

const ANGLE_TICK = {
  fill: "#a1a1aa",
  fontSize: 11,
  fontFamily: "Barlow Condensed, sans-serif",
  fontWeight: 700,
  letterSpacing: "0.1em",
};
const RADIUS_TICK = { fill: "#52525b", fontSize: 10 };
const RADIUS_DOMAIN = [0, 100];
const LEGEND_WRAPPER = { display: "none" };

export default function Compare() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [codeA, setCodeA] = useState(params.get("a") || "");
  const [codeB, setCodeB] = useState(params.get("b") || "");

  useEffect(() => {
    fetchTeams()
      .then((t) => {
        setTeams(t);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Sync state → URL (shareability)
  useEffect(() => {
    const next = new URLSearchParams();
    if (codeA) next.set("a", codeA);
    if (codeB) next.set("b", codeB);
    setParams(next, { replace: true });
  }, [codeA, codeB, setParams]);

  const teamA = useMemo(() => teams.find((t) => t.code === codeA), [teams, codeA]);
  const teamB = useMemo(() => teams.find((t) => t.code === codeB), [teams, codeB]);

  const setTeam = (side, code) => {
    if (side === "a") setCodeA(code || "");
    else setCodeB(code || "");
  };

  const swap = () => {
    setCodeA(codeB);
    setCodeB(codeA);
  };

  const radarData = useMemo(() => {
    return AXES.map(({ key, label }) => ({
      axis: label,
      [teamA?.code || "A"]: teamA ? teamA.mss.components[key] : 0,
      [teamB?.code || "B"]: teamB ? teamB.mss.components[key] : 0,
    }));
  }, [teamA, teamB]);

  const startSim = () => {
    if (!teamA || !teamB) return;
    navigate(`/match?a=${teamA.code}&b=${teamB.code}&k=0`);
  };

  return (
    <div className="min-h-screen pb-16">
      {/* Header band */}
      <section className="border-b border-zinc-900 bg-black">
        <div className="mx-auto max-w-[1400px] px-6 py-10">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-volt">
            Side-by-Side Analytics
          </div>
          <h1 className="mt-3 font-display text-4xl font-black uppercase leading-none tracking-tighter sm:text-5xl lg:text-6xl">
            Head-to-Head <span className="text-volt">Radar</span>
          </h1>
          <p className="mt-4 max-w-2xl font-body text-sm text-zinc-400 sm:text-base">
            Compare any two qualified nations across the 5 vectors that drive their Match
            Strength Score. Spot the strengths, expose the gaps, then simulate.
          </p>
        </div>
      </section>

      {/* Picker strip */}
      <section className="border-b border-zinc-900 bg-zinc-950">
        <div className="mx-auto max-w-[1400px] px-6 py-6">
          <div className="grid grid-cols-1 gap-px border border-zinc-800 bg-zinc-800 md:grid-cols-[1fr_auto_1fr]">
            <TeamPicker
              label="TEAM A"
              accent={COLOR_A}
              team={teamA}
              teams={teams}
              loading={loading}
              excludeCode={teamB?.code}
              onPick={(c) => setTeam("a", c)}
              onClear={() => setTeam("a", "")}
              testid="compare-pick-a"
            />
            <div className="flex items-center justify-center bg-black px-6 py-4">
              <button
                onClick={swap}
                disabled={!teamA && !teamB}
                data-testid="compare-swap-btn"
                className="flex items-center gap-2 border border-zinc-800 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 transition-colors hover:border-volt hover:text-volt disabled:opacity-30"
                title="Swap teams"
              >
                <ArrowLeftRight className="h-3.5 w-3.5" />
                Swap
              </button>
            </div>
            <TeamPicker
              label="TEAM B"
              accent={COLOR_B}
              team={teamB}
              teams={teams}
              loading={loading}
              excludeCode={teamA?.code}
              onPick={(c) => setTeam("b", c)}
              onClear={() => setTeam("b", "")}
              testid="compare-pick-b"
            />
          </div>
        </div>
      </section>

      {/* Main analytics */}
      <section className="mx-auto max-w-[1400px] px-6 py-10">
        {!teamA || !teamB ? (
          <EmptyState />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.1fr_1fr]">
              <RadarPanel teamA={teamA} teamB={teamB} data={radarData} />
              <VerdictPanel teamA={teamA} teamB={teamB} />
            </div>

            <div className="mt-6 grid grid-cols-1 gap-px border border-zinc-900 bg-zinc-900 md:grid-cols-2">
              <StatsList team={teamA} accent={COLOR_A} />
              <StatsList team={teamB} accent={COLOR_B} />
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t-2 border-volt bg-black p-6">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                  Ready?
                </div>
                <div className="mt-1 font-display text-2xl font-black uppercase tracking-tight">
                  Simulate {teamA.name} vs {teamB.name}
                </div>
              </div>
              <Button
                data-testid="compare-simulate-btn"
                onClick={startSim}
                className="h-12 rounded-none bg-volt px-6 font-display text-base font-bold uppercase tracking-widest text-black hover:bg-white"
              >
                <Zap className="mr-2 h-4 w-4" /> Simulate Match
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function EmptyState() {
  return (
    <div
      data-testid="compare-empty-state"
      className="flex flex-col items-center justify-center border border-dashed border-zinc-800 bg-black py-24 text-center"
    >
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">
        Pick two nations above
      </div>
      <div className="mt-3 font-display text-3xl font-black uppercase text-zinc-700">
        Awaiting Selection
      </div>
    </div>
  );
}

function RadarPanel({ teamA, teamB, data }) {
  return (
    <div
      data-testid="compare-radar"
      className="border border-zinc-900 bg-black p-6"
    >
      <div className="mb-3 flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          MSS Vector Radar
        </div>
        <div className="flex items-center gap-4 font-mono text-[10px] uppercase tracking-widest">
          <span className="flex items-center gap-2 text-bluez">
            <span className="inline-block h-2 w-3 bg-bluez" /> {teamA.code}
          </span>
          <span className="flex items-center gap-2 text-redz">
            <span className="inline-block h-2 w-3 bg-redz" /> {teamB.code}
          </span>
        </div>
      </div>
      <div className="h-[420px] w-full" style={{ minWidth: 0, minHeight: 320 }}>
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={320}>
          <RadarChart data={data} outerRadius="78%">
            <PolarGrid stroke="#27272a" />
            <PolarAngleAxis dataKey="axis" tick={ANGLE_TICK} />
            <PolarRadiusAxis
              angle={90}
              domain={RADIUS_DOMAIN}
              tick={RADIUS_TICK}
              stroke="#27272a"
            />
            <Radar
              name={teamA.name}
              dataKey={teamA.code}
              stroke={COLOR_A}
              fill={COLOR_A}
              fillOpacity={0.25}
              strokeWidth={2}
              isAnimationActive
            />
            <Radar
              name={teamB.name}
              dataKey={teamB.code}
              stroke={COLOR_B}
              fill={COLOR_B}
              fillOpacity={0.25}
              strokeWidth={2}
              isAnimationActive
            />
            <Legend wrapperStyle={LEGEND_WRAPPER} />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function VerdictPanel({ teamA, teamB }) {
  const diff = teamA.mss.mss - teamB.mss.mss;
  const favored = diff > 0 ? teamA : diff < 0 ? teamB : null;
  const underdog = diff > 0 ? teamB : diff < 0 ? teamA : null;
  const gap = Math.abs(diff).toFixed(1);

  const advantages = AXES.map(({ key, label }) => {
    const a = teamA.mss.components[key];
    const b = teamB.mss.components[key];
    return { label, key, a, b, diff: a - b };
  });

  return (
    <div className="border border-zinc-900 bg-zinc-950 p-6" data-testid="compare-verdict">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
        Algorithmic Verdict
      </div>
      <div className="mt-3 grid grid-cols-2 gap-px border border-zinc-900 bg-zinc-900">
        <MssBlock team={teamA} accent={COLOR_A} />
        <MssBlock team={teamB} accent={COLOR_B} />
      </div>

      <div className="mt-5">
        {favored ? (
          <div className="space-y-1">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-volt">
              MSS Edge
            </div>
            <div className="font-display text-2xl font-black uppercase leading-tight">
              {favored.name} <span className="text-volt">+{gap}</span>{" "}
              <span className="text-zinc-500">over</span> {underdog.name}
            </div>
          </div>
        ) : (
          <div className="font-display text-2xl font-black uppercase text-volt">
            Dead Even
          </div>
        )}
      </div>

      <div className="mt-6 space-y-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          Vector Deltas
        </div>
        {advantages.map((v) => (
          <DeltaBar key={v.key} item={v} teamA={teamA} teamB={teamB} />
        ))}
      </div>
    </div>
  );
}

function MssBlock({ team, accent }) {
  return (
    <div className="bg-black p-4">
      <div className="flex items-center gap-2">
        <Flag code={team.flag} className="h-4 w-6 object-cover" />
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
          {team.code}
        </span>
      </div>
      <div className="mt-2 font-display text-4xl font-black tabular-nums" style={{ color: accent }}>
        {team.mss.mss.toFixed(1)}
      </div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
        Match Strength Score
      </div>
    </div>
  );
}

function DeltaBar({ item, teamA, teamB }) {
  // Center bar: who leads in this vector by how much (0-100 each)
  const total = item.a + item.b || 1;
  const aPct = (item.a / total) * 100;
  return (
    <div>
      <div className="mb-1 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-zinc-500">
        <span className="text-bluez tabular-nums">{item.a.toFixed(0)}</span>
        <span>{item.label}</span>
        <span className="text-redz tabular-nums">{item.b.toFixed(0)}</span>
      </div>
      <div className="relative h-1.5 bg-zinc-900">
        <div
          className="absolute left-0 top-0 h-full bg-bluez transition-all"
          style={{ width: `${Math.max(2, Math.min(98, aPct))}%` }}
        />
        <div
          className="absolute right-0 top-0 h-full bg-redz transition-all"
          style={{ width: `${Math.max(2, Math.min(98, 100 - aPct))}%` }}
        />
      </div>
    </div>
  );
}

function StatsList({ team, accent }) {
  const rows = [
    ["Squad Value", `€${team.squad_value_m}M`],
    ["Top-5 League Players", team.top5_players],
    ["Avg Age", team.avg_age.toFixed(1)],
    ["Powerhouse Rating", team.powerhouse],
    ["Form Score", team.form],
    ["Confederation", team.confederation],
  ];
  return (
    <div className="bg-black p-6">
      <div className="mb-3 flex items-center gap-3">
        <Flag code={team.flag} className="h-6 w-9 object-cover" />
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
            {team.code} · {team.confederation}
          </div>
          <div className="font-display text-2xl font-black uppercase leading-none" style={{ color: accent }}>
            {team.name}
          </div>
        </div>
      </div>
      <dl className="grid grid-cols-2 gap-y-3">
        {rows.map(([label, val]) => (
          <div key={label}>
            <dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              {label}
            </dt>
            <dd className="font-display text-lg font-bold tabular-nums">{val}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function TeamPicker({ label, accent, team, teams, loading, excludeCode, onPick, onClear, testid }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    return teams
      .filter((t) => t.code !== excludeCode)
      .filter((t) => !query || t.name.toLowerCase().includes(query.toLowerCase()))
      .slice(0, 100);
  }, [teams, query, excludeCode]);

  if (loading) {
    return (
      <div className="flex items-center bg-black p-6">
        <div className="h-8 w-40 animate-pulse bg-zinc-900" />
      </div>
    );
  }

  return (
    <div className="relative flex items-center gap-4 bg-black p-6" data-testid={testid}>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em]" style={{ color: accent }}>
        {label}
      </div>
      {team ? (
        <>
          <Flag code={team.flag} className="h-8 w-12 object-cover" />
          <div className="flex-1">
            <div className="font-display text-2xl font-black uppercase leading-tight">
              {team.name}
            </div>
            <div className="font-mono text-xs text-zinc-500">
              MSS <span style={{ color: accent }}>{team.mss.mss.toFixed(1)}</span> · €{team.squad_value_m}M
            </div>
          </div>
          <button
            onClick={() => setOpen(true)}
            data-testid={`${testid}-change`}
            className="border border-zinc-800 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:border-volt hover:text-volt"
          >
            Change
          </button>
          <button
            onClick={onClear}
            data-testid={`${testid}-clear`}
            aria-label="Clear"
            className="text-zinc-500 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </>
      ) : (
        <button
          onClick={() => setOpen(true)}
          data-testid={`${testid}-open`}
          className="flex flex-1 items-center gap-3 text-zinc-600 hover:text-white"
        >
          <div className="h-8 w-12 border border-dashed border-zinc-700" />
          <span className="font-display text-xl font-extrabold uppercase tracking-tight">
            Select a team
          </span>
        </button>
      )}

      {open && (
        <PickerModal
          teams={filtered}
          query={query}
          onQuery={setQuery}
          onPick={(code) => {
            onPick(code);
            setOpen(false);
            setQuery("");
          }}
          onClose={() => {
            setOpen(false);
            setQuery("");
          }}
          testid={`${testid}-modal`}
        />
      )}
    </div>
  );
}

function PickerModal({ teams, query, onQuery, onPick, onClose, testid }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 px-4 py-16 backdrop-blur-sm"
      onClick={onClose}
      data-testid={testid}
      role="dialog"
      aria-modal="true"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl border border-zinc-800 bg-zinc-950"
      >
        <div className="flex items-center gap-3 border-b border-zinc-900 bg-black px-4 py-3">
          <Search className="h-4 w-4 text-zinc-500" />
          <Input
            autoFocus
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="Search nation..."
            data-testid={`${testid}-search`}
            className="rounded-none border-0 bg-transparent font-body text-sm focus-visible:ring-0"
          />
          <button onClick={onClose} className="text-zinc-500 hover:text-white" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {teams.length === 0 ? (
            <div className="p-6 text-center font-mono text-xs uppercase tracking-widest text-zinc-500">
              No teams match
            </div>
          ) : (
            <ul>
              {teams.map((t) => (
                <li key={t.code}>
                  <button
                    data-testid={`${testid}-option-${t.code}`}
                    onClick={() => onPick(t.code)}
                    className="flex w-full items-center gap-3 border-b border-zinc-900 px-4 py-3 text-left transition-colors hover:bg-zinc-900"
                  >
                    <Flag code={t.flag} className="h-5 w-7 object-cover" />
                    <div className="flex-1">
                      <div className="font-display text-base font-bold uppercase">{t.name}</div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                        {t.confederation} · €{t.squad_value_m}M
                      </div>
                    </div>
                    <div className="font-display text-lg font-black tabular-nums text-volt">
                      {t.mss.mss.toFixed(0)}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
